import React from "react";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FormatAlignCenterIcon from "@mui/icons-material/FormatAlignCenter";
import FormatAlignLeftIcon from "@mui/icons-material/FormatAlignLeft";
import FormatAlignRightIcon from "@mui/icons-material/FormatAlignRight";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import {
    Box,
    Button,
    Divider,
    FormControlLabel,
    IconButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Select,
    Stack,
    Switch,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from "@mui/material";
import {useTranslation} from "react-i18next";
import {
    createFolioId,
    createFolioTotal,
    FOLIO_ALIGNS,
    FOLIO_COLUMN_VARIABLES,
    FOLIO_PROTECTED_TOTAL_KEY,
    FOLIO_TOTAL_VARIABLES,
    getFolioColumnVariableLabel,
    getFolioDefaultAlign,
    getFolioTableAttrs,
    getFolioTotalVariableLabel,
} from "../../../blocks/folioTable/printoutFolioTableOptions";
import type {
    FolioAlign,
    FolioColumn,
    FolioSortDirection,
    FolioTextStyle,
    FolioTotal,
    PrintoutFolioTableAttrs,
} from "../../../blocks/folioTable/printoutFolioTableOptions";
import {PanelField} from "../PrintoutPanelSection";
import PrintoutSettingsAccordion from "./PrintoutSettingsAccordion";
import {updateSelectedPrintoutBlockAttributes} from "./printoutBlockAttributes";
import type {PrintoutBlockSettingsProps} from "./printoutBlockAttributes";

interface PickableVariable {
    key: string;
    label: string;
}

const ALIGN_ICONS: Record<FolioAlign, React.ReactNode> = {
    center: <FormatAlignCenterIcon fontSize={'small'}/>,
    left: <FormatAlignLeftIcon fontSize={'small'}/>,
    right: <FormatAlignRightIcon fontSize={'small'}/>,
};

const DEFAULT_DATE_SORT_KEYS = new Set(['folioItemStartDate', 'folioItemStartTime']);

const RowCaption = ({children}: {children: React.ReactNode}) => (
    <Typography color={'text.secondary'} sx={{fontSize: 11, lineHeight: 1.4}}>
        {children}
    </Typography>
);

const FormatToggle = ({disabled, onChange, value}: {
    disabled?: boolean;
    onChange: (style: FolioTextStyle) => void;
    value: FolioTextStyle;
}) => {
    const {t} = useTranslation();
    const selected = [value.bold ? 'bold' : '', value.italic ? 'italic' : ''].filter(Boolean);

    return (
        <ToggleButtonGroup
            disabled={disabled}
            onChange={(_event, next: string[]) => onChange({
                bold: next.includes('bold'),
                italic: next.includes('italic'),
            })}
            size={'small'}
            value={selected}
        >
            <ToggleButton aria-label={t('Bold')} value={'bold'}>
                <FormatBoldIcon fontSize={'small'}/>
            </ToggleButton>
            <ToggleButton aria-label={t('Italic')} value={'italic'}>
                <FormatItalicIcon fontSize={'small'}/>
            </ToggleButton>
        </ToggleButtonGroup>
    );
};

type FolioItemKind = "column" | "total";

interface FolioMenuState {
    anchorEl: HTMLElement;
    id: string;
    kind: FolioItemKind;
}

const HideWhenUnresolvedToggle = ({disabled, locked, onChange, value}: {
    disabled?: boolean;
    locked: boolean;
    onChange: (hideWhenUnresolved: boolean) => void;
    value: boolean;
}) => {
    const {t} = useTranslation();
    const title = locked
        ? t('The total always prints')
        : value
            ? t('Hidden when the value is empty')
            : t('Prints a dash when the value is empty');

    return (
        <Tooltip title={title}>
            <span>
                <IconButton
                    aria-label={title}
                    disabled={disabled || locked}
                    onClick={() => onChange(!value)}
                    size={'small'}
                >
                    {value ? <VisibilityOffOutlinedIcon fontSize={'small'}/> : <VisibilityOutlinedIcon fontSize={'small'}/>}
                </IconButton>
            </span>
        </Tooltip>
    );
};

const AddMenuButton = ({disabled, label, onAdd, usedKeys, variables}: {
    disabled?: boolean;
    label: string;
    onAdd: (key: string) => void;
    usedKeys: Set<string>;
    variables: readonly PickableVariable[];
}) => {
    const {t} = useTranslation();
    const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
    const options = variables.filter(variable => !usedKeys.has(variable.key));

    if (options.length === 0) return null;

    return (
        <Box>
            <Button
                disabled={disabled}
                onClick={(event) => setAnchorEl(event.currentTarget)}
                size={'small'}
                startIcon={<AddIcon/>}
                sx={{textTransform: 'none'}}
                variant={'text'}
            >
                {t(label)}
            </Button>
            <Menu
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                open={Boolean(anchorEl)}
                slotProps={{paper: {sx: {maxHeight: 360}}}}
            >
                {options.map(variable => (
                    <MenuItem
                        key={variable.key}
                        onClick={() => {
                            onAdd(variable.key);
                            setAnchorEl(null);
                        }}
                    >
                        {t(variable.label)}
                    </MenuItem>
                ))}
            </Menu>
        </Box>
    );
};

export function FolioTableSettings({block, disabled, editor}: PrintoutBlockSettingsProps) {
    const {t} = useTranslation();

    const attrs = getFolioTableAttrs(block.node.attrs as PrintoutFolioTableAttrs);
    const [expandedColumnId, setExpandedColumnId] = React.useState<string | null>(attrs.columns[0]?.id ?? null);
    const [expandedTotalId, setExpandedTotalId] = React.useState<string | null>(null);
    const [itemMenu, setItemMenu] = React.useState<FolioMenuState | null>(null);
    const usedColumnKeys = new Set(attrs.columns.map(column => column.variableKey));
    const usedTotalKeys = new Set(attrs.totals.map(total => total.variableKey));
    const menuColumnIndex = itemMenu?.kind === 'column'
        ? attrs.columns.findIndex(column => column.id === itemMenu.id)
        : -1;
    const menuTotalIndex = itemMenu?.kind === 'total'
        ? attrs.totals.findIndex(total => total.id === itemMenu.id)
        : -1;
    const menuColumn = menuColumnIndex >= 0 ? attrs.columns[menuColumnIndex] : null;
    const menuTotal = menuTotalIndex >= 0 ? attrs.totals[menuTotalIndex] : null;

    React.useEffect(() => {
        if (
            expandedColumnId === null
            || attrs.columns.some(column => column.id === expandedColumnId)
        ) return;
        setExpandedColumnId(attrs.columns[0]?.id ?? null);
    }, [attrs.columns, expandedColumnId]);

    React.useEffect(() => {
        if (
            expandedTotalId === null
            || attrs.totals.some(total => total.id === expandedTotalId)
        ) return;
        setExpandedTotalId(null);
    }, [attrs.totals, expandedTotalId]);

    const update = (next: Partial<PrintoutFolioTableAttrs>) =>
        updateSelectedPrintoutBlockAttributes(editor, block, next);

    const setColumns = (columns: FolioColumn[]) => update({columns});
    const setTotals = (totals: FolioTotal[]) => update({totals});

    const updateColumn = (id: string, patch: Partial<FolioColumn>) => setColumns(
        attrs.columns.map(column => (column.id === id ? {...column, ...patch} : column)),
    );

    const updateTotal = (id: string, patch: Partial<FolioTotal>) => setTotals(
        attrs.totals.map(total => (total.id === id ? {...total, ...patch} : total)),
    );

    // Rebind defaults without replacing authored labels or alignment.
    const rebindColumn = (column: FolioColumn, variableKey: string) => {
        const labelWasDefault = !column.label.trim()
            || column.label === getFolioColumnVariableLabel(column.variableKey);
        const alignWasDefault = column.align === getFolioDefaultAlign(column.variableKey);

        updateColumn(column.id, {
            align: alignWasDefault ? getFolioDefaultAlign(variableKey) : column.align,
            label: labelWasDefault ? getFolioColumnVariableLabel(variableKey) : column.label,
            variableKey,
        });
    };

    const rebindTotal = (total: FolioTotal, variableKey: string) => {
        const labelWasDefault = !total.label.trim()
            || total.label === getFolioTotalVariableLabel(total.variableKey);

        updateTotal(total.id, {
            label: labelWasDefault ? getFolioTotalVariableLabel(variableKey) : total.label,
            variableKey,
        });
    };

    const addColumn = (variableKey: string) => {
        const column: FolioColumn = {
            align: getFolioDefaultAlign(variableKey),
            cellFormat: {bold: false, italic: false},
            headerFormat: {bold: false, italic: false},
            id: createFolioId(),
            label: getFolioColumnVariableLabel(variableKey),
            variableKey,
        };

        setColumns([...attrs.columns, column]);
        setExpandedColumnId(column.id);
    };

    const addTotal = (variableKey: string) => {
        const total = createFolioTotal(variableKey);

        setTotals([...attrs.totals, total]);
        setExpandedTotalId(total.id);
    };

    const move = <T, >(items: T[], index: number, offset: number): T[] | null => {
        const target = index + offset;
        if (target < 0 || target >= items.length) return null;

        const next = [...items];
        [next[index], next[target]] = [next[target], next[index]];

        return next;
    };

    const deleteColumn = (index: number) => {
        if (attrs.columns.length <= 1 || index < 0) return;
        const columns = attrs.columns.filter((_column, columnIndex) => columnIndex !== index);
        setColumns(columns);
        setExpandedColumnId(current => (
            current === attrs.columns[index].id
                ? columns[Math.min(index, columns.length - 1)]?.id ?? null
                : current
        ));
    };

    const deleteTotal = (index: number) => {
        if (index < 0 || attrs.totals[index].variableKey === FOLIO_PROTECTED_TOTAL_KEY) return;
        const totals = attrs.totals.filter((_total, totalIndex) => totalIndex !== index);
        setTotals(totals);
        setExpandedTotalId(current => (
            current === attrs.totals[index].id
                ? totals[Math.min(index, totals.length - 1)]?.id ?? null
                : current
        ));
    };

    const resetColumnFormatting = (column: FolioColumn) => updateColumn(column.id, {
        align: getFolioDefaultAlign(column.variableKey),
        cellFormat: {bold: false, italic: false},
        headerFormat: {bold: false, italic: false},
    });

    const resetTotalFormatting = (total: FolioTotal) => updateTotal(total.id, {
        format: {bold: false, italic: false},
    });

    // Keep obsolete stored keys editable until the author replaces them.
    const pickable = (
        variables: readonly PickableVariable[],
        usedKeys: Set<string>,
        currentKey: string,
        labelOf: (key: string) => string,
    ): PickableVariable[] => {
        const options = variables.filter(variable => variable.key === currentKey || !usedKeys.has(variable.key));

        return options.some(variable => variable.key === currentKey)
            ? options
            : [{key: currentKey, label: labelOf(currentKey)}, ...options];
    };

    const menuIndex = itemMenu?.kind === 'column' ? menuColumnIndex : menuTotalIndex;
    const menuLength = itemMenu?.kind === 'column' ? attrs.columns.length : attrs.totals.length;
    const menuItemExists = Boolean(menuColumn || menuTotal);
    const menuCanRemove = itemMenu?.kind === 'column'
        ? attrs.columns.length > 1 && menuColumnIndex >= 0
        : Boolean(menuTotal && menuTotal.variableKey !== FOLIO_PROTECTED_TOTAL_KEY);

    const moveMenuItem = (offset: number) => {
        if (itemMenu?.kind === 'column') {
            const columns = move(attrs.columns, menuColumnIndex, offset);
            if (columns) setColumns(columns);
        } else if (itemMenu?.kind === 'total') {
            const totals = move(attrs.totals, menuTotalIndex, offset);
            if (totals) setTotals(totals);
        }
        setItemMenu(null);
    };

    const resetMenuItem = () => {
        if (menuColumn) resetColumnFormatting(menuColumn);
        if (menuTotal) resetTotalFormatting(menuTotal);
        setItemMenu(null);
    };

    const removeMenuItem = () => {
        if (itemMenu?.kind === 'column') deleteColumn(menuColumnIndex);
        if (itemMenu?.kind === 'total') deleteTotal(menuTotalIndex);
        setItemMenu(null);
    };

    return (
        <>
            <PanelField label={t('Sort by')}>
                <Select
                    displayEmpty
                    disabled={disabled}
                    fullWidth
                    onChange={(event) => update({
                        sort: {...attrs.sort, columnId: String(event.target.value) || null},
                    })}
                    size={'small'}
                    value={attrs.sort.columnId ?? ''}
                >
                    <MenuItem value={''}>{t('Start date')}</MenuItem>
                    {attrs.columns.filter(column => (
                        column.id === attrs.sort.columnId
                        || !DEFAULT_DATE_SORT_KEYS.has(column.variableKey)
                    )).map(column => (
                        <MenuItem key={column.id} value={column.id}>
                            {column.label || getFolioColumnVariableLabel(column.variableKey)}
                        </MenuItem>
                    ))}
                </Select>

                <Select
                    disabled={disabled}
                    fullWidth
                    onChange={(event) => update({
                        sort: {...attrs.sort, direction: event.target.value as FolioSortDirection},
                    })}
                    size={'small'}
                    value={attrs.sort.direction}
                >
                    <MenuItem value={'asc'}>{t('Ascending · earliest first')}</MenuItem>
                    <MenuItem value={'desc'}>{t('Descending · latest first')}</MenuItem>
                </Select>
            </PanelField>

            <Stack gap={1}>
                <FormControlLabel
                    control={(
                        <Switch
                            checked={attrs.groupByDate}
                            disabled={disabled}
                            onChange={(event) => update({groupByDate: event.target.checked})}
                            size={'small'}
                        />
                    )}
                    label={(
                        <Stack gap={0.25}>
                            <Typography variant={'body2'}>{t('Group items by day')}</Typography>
                            <RowCaption>
                                {t('A date header opens each day; items without a date follow at the end.')}
                            </RowCaption>
                        </Stack>
                    )}
                    sx={{alignItems: 'flex-start', ml: 0, mr: 0, '& .MuiSwitch-root': {mt: 0.25}}}
                />

                <FormControlLabel
                    control={(
                        <Switch
                            checked={attrs.excludeSyntheticRows}
                            disabled={disabled}
                            onChange={(event) => update({excludeSyntheticRows: event.target.checked})}
                            size={'small'}
                        />
                    )}
                    label={(
                        <Stack gap={0.25}>
                            <Typography variant={'body2'}>
                                {t('Hide category and fixed-charge summary rows')}
                            </Typography>
                            <RowCaption>
                                {t('The billing system adds these to the item list; printing them shows each amount twice.')}
                            </RowCaption>
                        </Stack>
                    )}
                    sx={{alignItems: 'flex-start', ml: 0, mr: 0, '& .MuiSwitch-root': {mt: 0.25}}}
                />
            </Stack>

            <PanelField label={t('Columns')}>
                <Stack gap={1}>
                    {attrs.columns.map(column => {
                        const variables = pickable(
                            FOLIO_COLUMN_VARIABLES,
                            usedColumnKeys,
                            column.variableKey,
                            getFolioColumnVariableLabel,
                        );

                        return (
                            <PrintoutSettingsAccordion
                                actionsLabel={t('Column actions')}
                                disabled={disabled}
                                expanded={expandedColumnId === column.id}
                                id={column.id}
                                idPrefix={'folio-column'}
                                key={column.id}
                                onOpenMenu={(anchorEl) => setItemMenu({anchorEl, id: column.id, kind: 'column'})}
                                onToggle={() => setExpandedColumnId(current => (
                                    current === column.id ? null : column.id
                                ))}
                                subtitle={t(getFolioColumnVariableLabel(column.variableKey))}
                                title={column.label.trim() || t(getFolioColumnVariableLabel(column.variableKey))}
                            >
                                <Stack gap={2}>
                                    <PanelField label={t('Field')}>
                                        <Select
                                            disabled={disabled}
                                            fullWidth
                                            onChange={(event) => rebindColumn(column, String(event.target.value))}
                                            size={'small'}
                                            value={column.variableKey}
                                        >
                                            {variables.map(variable => (
                                                <MenuItem key={variable.key} value={variable.key}>
                                                    {t(variable.label)}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </PanelField>

                                    <PanelField label={t('Label')}>
                                        <TextField
                                            disabled={disabled}
                                            fullWidth
                                            onChange={(event) => updateColumn(column.id, {label: event.target.value})}
                                            placeholder={t(getFolioColumnVariableLabel(column.variableKey))}
                                            size={'small'}
                                            value={column.label}
                                        />
                                    </PanelField>

                                    <PanelField label={t('Formatting')}>
                                        <Stack alignItems={'flex-start'} direction={'row'} flexWrap={'wrap'} gap={1.5}>
                                            <Stack gap={0.25}>
                                                <RowCaption>{t('Alignment')}</RowCaption>
                                                <ToggleButtonGroup
                                                    disabled={disabled}
                                                    exclusive
                                                    onChange={(_event, align: FolioAlign | null) => {
                                                        if (align) updateColumn(column.id, {align});
                                                    }}
                                                    size={'small'}
                                                    value={column.align}
                                                >
                                                    {FOLIO_ALIGNS.map(align => (
                                                        <ToggleButton aria-label={t(align)} key={align} value={align}>
                                                            {ALIGN_ICONS[align]}
                                                        </ToggleButton>
                                                    ))}
                                                </ToggleButtonGroup>
                                            </Stack>

                                            <Stack gap={0.25}>
                                                <RowCaption>{t('Label')}</RowCaption>
                                                <FormatToggle
                                                    disabled={disabled}
                                                    onChange={(headerFormat) => updateColumn(column.id, {headerFormat})}
                                                    value={column.headerFormat}
                                                />
                                            </Stack>

                                            <Stack gap={0.25}>
                                                <RowCaption>{t('Values')}</RowCaption>
                                                <FormatToggle
                                                    disabled={disabled}
                                                    onChange={(cellFormat) => updateColumn(column.id, {cellFormat})}
                                                    value={column.cellFormat}
                                                />
                                            </Stack>
                                        </Stack>
                                    </PanelField>
                                </Stack>
                            </PrintoutSettingsAccordion>
                        );
                    })}
                </Stack>

                <AddMenuButton
                    disabled={disabled}
                    label={'Add column'}
                    onAdd={addColumn}
                    usedKeys={usedColumnKeys}
                    variables={FOLIO_COLUMN_VARIABLES}
                />
            </PanelField>

            <PanelField label={t('Totals')}>
                <Stack gap={1}>
                    {attrs.totals.map(total => {
                        const isProtected = total.variableKey === FOLIO_PROTECTED_TOTAL_KEY;
                        const variables = pickable(
                            FOLIO_TOTAL_VARIABLES,
                            usedTotalKeys,
                            total.variableKey,
                            getFolioTotalVariableLabel,
                        );

                        return (
                            <PrintoutSettingsAccordion
                                actionsLabel={t('Total actions')}
                                disabled={disabled}
                                expanded={expandedTotalId === total.id}
                                id={total.id}
                                idPrefix={'folio-total'}
                                key={total.id}
                                onOpenMenu={(anchorEl) => setItemMenu({anchorEl, id: total.id, kind: 'total'})}
                                onToggle={() => setExpandedTotalId(current => (
                                    current === total.id ? null : total.id
                                ))}
                                subtitle={t(getFolioTotalVariableLabel(total.variableKey))}
                                title={total.label.trim() || t(getFolioTotalVariableLabel(total.variableKey))}
                            >
                                <Stack gap={2}>
                                    <PanelField label={t('Field')}>
                                        <Tooltip title={isProtected ? t('The total cannot be removed') : ''}>
                                            <Box>
                                                <Select
                                                    disabled={disabled || isProtected}
                                                    fullWidth
                                                    onChange={(event) => rebindTotal(total, String(event.target.value))}
                                                    size={'small'}
                                                    value={total.variableKey}
                                                >
                                                    {variables.map(variable => (
                                                        <MenuItem key={variable.key} value={variable.key}>
                                                            {t(variable.label)}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </Box>
                                        </Tooltip>
                                    </PanelField>

                                    <PanelField label={t('Label')}>
                                        <TextField
                                            disabled={disabled}
                                            fullWidth
                                            onChange={(event) => updateTotal(total.id, {label: event.target.value})}
                                            placeholder={t(getFolioTotalVariableLabel(total.variableKey))}
                                            size={'small'}
                                            value={total.label}
                                        />
                                    </PanelField>

                                    <PanelField label={t('Formatting')}>
                                        <Stack alignItems={'flex-start'} direction={'row'} flexWrap={'wrap'} gap={1.5}>
                                            <Stack gap={0.25}>
                                                <RowCaption>{t('Emphasis')}</RowCaption>
                                                <FormatToggle
                                                    disabled={disabled}
                                                    onChange={(format) => updateTotal(total.id, {format})}
                                                    value={total.format}
                                                />
                                            </Stack>

                                            <Stack gap={0.25}>
                                                <RowCaption>{t('When empty')}</RowCaption>
                                                <HideWhenUnresolvedToggle
                                                    disabled={disabled}
                                                    locked={isProtected}
                                                    onChange={(hideWhenUnresolved) => updateTotal(total.id, {hideWhenUnresolved})}
                                                    value={total.hideWhenUnresolved === true}
                                                />
                                            </Stack>
                                        </Stack>
                                    </PanelField>
                                </Stack>
                            </PrintoutSettingsAccordion>
                        );
                    })}
                </Stack>

                <AddMenuButton
                    disabled={disabled}
                    label={'Add total'}
                    onAdd={addTotal}
                    usedKeys={usedTotalKeys}
                    variables={FOLIO_TOTAL_VARIABLES}
                />
            </PanelField>

            <Menu
                anchorEl={itemMenu?.anchorEl ?? null}
                onClose={() => setItemMenu(null)}
                open={Boolean(itemMenu)}
            >
                <MenuItem
                    disabled={!menuItemExists || menuIndex <= 0}
                    onClick={() => moveMenuItem(-1)}
                >
                    <ListItemIcon><KeyboardArrowUpIcon fontSize={'small'}/></ListItemIcon>
                    <ListItemText>{t('Sort up')}</ListItemText>
                </MenuItem>
                <MenuItem
                    disabled={!menuItemExists || menuIndex < 0 || menuIndex >= menuLength - 1}
                    onClick={() => moveMenuItem(1)}
                >
                    <ListItemIcon><KeyboardArrowDownIcon fontSize={'small'}/></ListItemIcon>
                    <ListItemText>{t('Sort down')}</ListItemText>
                </MenuItem>
                <Divider/>
                <MenuItem disabled={!menuItemExists} onClick={resetMenuItem}>
                    <ListItemIcon><RestartAltIcon fontSize={'small'}/></ListItemIcon>
                    <ListItemText>{t('Reset formatting')}</ListItemText>
                </MenuItem>
                <MenuItem
                    disabled={!menuCanRemove}
                    onClick={removeMenuItem}
                    sx={{color: 'error.main'}}
                >
                    <ListItemIcon sx={{color: 'inherit'}}>
                        <DeleteOutlineIcon fontSize={'small'}/>
                    </ListItemIcon>
                    <ListItemText>
                        {t(itemMenu?.kind === 'total' ? 'Remove total' : 'Remove column')}
                    </ListItemText>
                </MenuItem>
            </Menu>
        </>
    );
}

export default FolioTableSettings;
